import { useState, useEffect, useCallback, useMemo } from 'react';
import { transporterAPI } from '@/services/api';
import { useDebounce } from './useDebounce';

export const useTransporterRequests = (filters = {}) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });

  // Stabiliser les filtres pour éviter les boucles infinies
  const stableFilters = useMemo(() => filters, [
    filters.search,
    filters.urgency,
    filters.status,
    filters.from_location,
    filters.to_location
  ]);

  // Debouncer les filtres pour éviter les requêtes trop fréquentes
  const debouncedFilters = useDebounce(stableFilters, 300);

  const fetchRequests = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      // Filtrer les paramètres vides ou "all"
      const cleanFilters = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([key, value]) => 
          value !== '' && value !== 'all' && value != null
        )
      );
      
      const params = {
        page,
        ...cleanFilters,
      };
      
      const response = await transporterAPI.getAvailableRequests(params);
      setRequests(response.data.results || response.data);
      setPagination({
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
      });
    } catch (err) {
      console.error('Erreur lors du chargement des demandes:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  const makeOffer = useCallback(async (requestId, offerData) => {
    try {
      const response = await transporterAPI.makeOffer(requestId, offerData);
      // Recharger les demandes après avoir fait une offre
      fetchRequests();
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Erreur lors de la création de l\'offre:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Erreur lors de la création de l\'offre' 
      };
    }
  }, [fetchRequests]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return {
    requests,
    loading,
    error,
    pagination,
    fetchRequests,
    makeOffer,
  };
};
